import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { adminApi } from '../../../services/api';
import CallQueuePanel from '../CallQueuePanel';

/**
 * LeadAndOpportunityTracker - Component for managing lead & opportunity tracking data
 *
 * NOTE: This component works with SNAPSHOT data stored in the Lead/Opportunity's JSONFields,
 * NOT with normalized Prospect model data.
 *
 * @param {Object} entity - SalesLead or SalesOpportunity object (null/undefined for create mode)
 * @param {Function} onUpdate - Callback when entity is updated (edit mode)
 * @param {Function} onChange - Callback when form values change (create mode) - receives form data object
 * @param {string} entityType - 'lead' or 'opportunity'
 * @param {boolean} isCreateMode - Whether this is for creating a new entity
 */
const LeadAndOpportunityTracker = ({ entity, onUpdate, onChange, entityType: entityTypeProp, isCreateMode = false }) => {
  const location = useLocation();

  // Auto-detect entity type from URL if not provided
  const entityType = useMemo(() => {
    if (entityTypeProp) return entityTypeProp;

    // Check URL path to determine entity type
    if (location.pathname.includes('/admin/sales/leads')) {
      return 'lead';
    } else if (location.pathname.includes('/admin/sales/opportunities')) {
      return 'opportunity';
    }
    // Default to 'opportunity' for backward compatibility with /admin/sales/prospects
    return 'opportunity';
  }, [entityTypeProp, location.pathname]);
  // Settings state
  const [contactStatuses, setContactStatuses] = useState([]);  // full list, used for history lookup
  const [priorities, setPriorities] = useState([]);
  const [users, setUsers] = useState([]);

  // Lead tracking fields (current values)
  const [assignedToUserType, setAssignedToUserType] = useState(null); // 'sales' or 'ankr'
  const [assignedToUserUuid, setAssignedToUserUuid] = useState(null); // SalesUser or AnkrUser UUID
  const [leadStatus, setLeadStatus] = useState('New');
  const [priority, setPriority] = useState('Medium');
  const [leadScore, setLeadScore] = useState(5);
  const [lastContactedDate, setLastContactedDate] = useState('');
  const [followUpDate, setNextFollowUpDate] = useState('');
  const [salesNotes, setSalesNotes] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  // const [queuePanelOpen, setQueuePanelOpen] = useState(false);

  // Fresh entity data from DB
  const [freshEntityData, setFreshEntityData] = useState(null);

  // Only show stages that apply to this entity type in the status dropdown
  const scopedStatuses = useMemo(
    () => contactStatuses.filter(s => s.applies_to === entityType || s.applies_to === 'both'),
    [contactStatuses, entityType]
  );

  // Only show users from the entity's organization (if org is assigned)
  const scopedUsers = useMemo(() => {
    // If entity has an organization, filter users to that org only
    const entityOrgId = freshEntityData?.organization?.id || entity?.organization?.id;
    if (entityOrgId) {
      return users.filter(user =>
        user.organization_memberships?.some(
          membership => membership.organization?.id === entityOrgId
        )
      );
    }
    // If no organization assigned, show all users
    return users;
  }, [users, freshEntityData, entity]);

  // Scroll state for button groups
  const stageScrollRef = useRef(null);
  const priorityScrollRef = useRef(null);
  const [canScrollStageLeft, setCanScrollStageLeft] = useState(false);
  const [canScrollStageRight, setCanScrollStageRight] = useState(false);
  const [canScrollPriorityLeft, setCanScrollPriorityLeft] = useState(false);
  const [canScrollPriorityRight, setCanScrollPriorityRight] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [statusesRes, prioritiesRes, usersRes] = await Promise.all([
          adminApi.getLeadOpportunityStatuses(),
          adminApi.getPriorities(),
          adminApi.getSalesUsers()
        ]);
        setContactStatuses(statusesRes.data || []);
        setPriorities(prioritiesRes.data || []);
        setUsers(usersRes.data?.users || []);
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, []);

  // Load fresh entity data from DB
  const loadFreshEntityData = async () => {
    if (!entity?.id || isCreateMode) return null;

    try {
      setLoading(true);
      // Call the appropriate API based on entity type
      const response = entityType === 'lead'
        ? await adminApi.getSalesLead(entity.id)
        : await adminApi.getSalesOpportunity(entity.id);

      if (response.data) {
        setFreshEntityData(response.data);
        return response.data;
      }
      return null;
    } catch (err) {
      console.error('Error loading entity data:', err);
      setError('Failed to load entity data');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Load data when entity changes (skip in create mode)
  useEffect(() => {
    if (!isCreateMode && entity?.id) {
      loadFreshEntityData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id, isCreateMode]);

  // Initialize form fields from fresh DB data
  useEffect(() => {
    if (freshEntityData) {
      setAssignedToUserType(freshEntityData.assigned_to_user_type || null);
      setAssignedToUserUuid(freshEntityData.assigned_to_user_uuid || null);
      setLeadStatus(freshEntityData.contact_status_detail?.name || freshEntityData.contact_status || 'New');
      setPriority(freshEntityData.priority_detail?.name || freshEntityData.priority || 'Medium');
      setLeadScore(freshEntityData.lead_score || 5);
      setLastContactedDate(freshEntityData.last_contacted_date || '');
      setNextFollowUpDate(freshEntityData.follow_up_date || '');
      setSalesNotes(freshEntityData.tracking_notes || '');
    }
  }, [freshEntityData]);

  // In create mode, pass form data to parent whenever it changes
  useEffect(() => {
    if (isCreateMode && onChange) {
      const statusObj = scopedStatuses.find(s => s.name === leadStatus);
      const priorityObj = priorities.find(p => p.name === priority);

      const data = {
        assigned_to_user_type: assignedToUserType,
        assigned_to_user_uuid: assignedToUserUuid,
        contact_status: statusObj?.id,
        priority: priorityObj?.id,
        lead_score: leadScore,
        last_contacted_date: lastContactedDate || null,
        follow_up_date: followUpDate || null,
        notes: salesNotes
      };

      // Auto-assign organization from selected user's organization (if user is selected)
      if (assignedToUserUuid) {
        const selectedUser = scopedUsers.find(u => u.id === assignedToUserUuid);
        if (selectedUser?.organization_memberships?.[0]?.organization?.id) {
          data.organization_id = selectedUser.organization_memberships[0].organization.id;
        }
      }

      onChange(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, assignedToUserType, assignedToUserUuid, leadStatus, priority, leadScore, lastContactedDate, followUpDate, salesNotes]);

  const handleSave = async () => {
    if (isCreateMode) return; // Create mode handled by parent

    try {
      setLoading(true);

      // EDIT MODE - Build updates object with only changed fields
      const updates = {};

      // Check what changed - assigned_to uses user_type + UUID pattern
      const currentUserType = freshEntityData?.assigned_to_user_type || null;
      const currentUserUuid = freshEntityData?.assigned_to_user_uuid || null;
      if (assignedToUserType !== currentUserType || assignedToUserUuid !== currentUserUuid) {
        updates.assigned_to_user_type = assignedToUserType;
        updates.assigned_to_user_uuid = assignedToUserUuid;

        // Auto-assign organization if lead doesn't have one and user is being assigned
        if (assignedToUserUuid && !freshEntityData?.organization) {
          const selectedUser = scopedUsers.find(u => u.id === assignedToUserUuid);
          if (selectedUser?.organization_memberships?.[0]?.organization?.id) {
            updates.organization_id = selectedUser.organization_memberships[0].organization.id;
          }
        }
      }

      // Convert status name to ID
      const currentStatusName = freshEntityData?.contact_status_detail?.name || freshEntityData?.contact_status || '';
      if (leadStatus !== currentStatusName) {
        const statusObj = scopedStatuses.find(s => s.name === leadStatus);
        if (statusObj) {
          updates.contact_status = statusObj.id;
        }
      }

      // Convert priority name to ID
      const currentPriorityName = freshEntityData?.priority_detail?.name || freshEntityData?.priority || 'Medium';
      if (priority !== currentPriorityName) {
        const priorityObj = priorities.find(p => p.name === priority);
        if (priorityObj) {
          updates.priority = priorityObj.id;
        }
      }

      if (leadScore !== (freshEntityData?.lead_score || 5)) {
        updates.lead_score = leadScore;
      }
      if (lastContactedDate !== (freshEntityData?.last_contacted_date || '')) {
        updates.last_contacted_date = lastContactedDate;
      }
      if (followUpDate !== (freshEntityData?.follow_up_date || '')) {
        updates.follow_up_date = followUpDate;
      }
      if (salesNotes !== (freshEntityData?.tracking_notes || '')) {
        updates.notes = salesNotes;
      }

      if (Object.keys(updates).length === 0) {
        setSuccess('No changes to save');
        setTimeout(() => setSuccess(null), 3000);
        return;
      }

      // Save to backend using the appropriate API
      if (entityType === 'lead') {
        await adminApi.updateSalesLead(entity.id, updates);
      } else {
        await adminApi.updateSalesOpportunity(entity.id, updates);
      }

      // Refresh data and get the updated entity
      const updatedData = await loadFreshEntityData();

      // Notify parent component with the updated entity
      if (onUpdate && updatedData) {
        onUpdate(updatedData);
      }

      setSuccess(`Updated ${Object.keys(updates).length} field(s)`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to update ${entityType}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleTodayClick = () => {
    setLastContactedDate(new Date().toISOString().split('T')[0]);
  };

  const handleNextWeekClick = () => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    setNextFollowUpDate(nextWeek.toISOString().split('T')[0]);
  };

  // Check scroll state for button groups
  const checkScrollState = (ref, setLeft, setRight) => {
    if (!ref.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = ref.current;
    setLeft(scrollLeft > 0);
    setRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  // Scroll button groups
  const scroll = (ref, direction) => {
    if (!ref.current) return;
    const scrollAmount = 150;
    ref.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  // Update scroll state on mount and when statuses/priorities change
  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollState(stageScrollRef, setCanScrollStageLeft, setCanScrollStageRight);
      checkScrollState(priorityScrollRef, setCanScrollPriorityLeft, setCanScrollPriorityRight);
    }, 100);
    return () => clearTimeout(timer);
  }, [scopedStatuses, priorities]);

  // Check if any fields changed (only in edit mode)
  const currentStatusName = freshEntityData?.contact_status_detail?.name || freshEntityData?.contact_status || 'New';
  const currentPriorityName = freshEntityData?.priority_detail?.name || freshEntityData?.priority || 'Medium';
  const currentUserType = freshEntityData?.assigned_to_user_type || null;
  const currentUserUuid = freshEntityData?.assigned_to_user_uuid || null;
  const hasChanges = assignedToUserType !== currentUserType ||
    assignedToUserUuid !== currentUserUuid ||
    leadStatus !== currentStatusName ||
    priority !== currentPriorityName ||
    leadScore !== (freshEntityData?.lead_score || 5) ||
    lastContactedDate !== (freshEntityData?.last_contacted_date || '') ||
    followUpDate !== (freshEntityData?.follow_up_date || '') ||
    salesNotes !== (freshEntityData?.tracking_notes || '');

  // Get tracking history from activities (newest first) - only in edit mode
  const trackingHistory = freshEntityData?.activities || [];

  // Determine title based on entity type and mode
  const panelTitle = isCreateMode
    ? (entityType === 'lead' ? 'Take Action' : 'Take Action')
    : (entityType === 'lead' ? 'Lead Tracker' :
       entityType === 'opportunity' ? 'Opportunity Tracker' :
       'Prospect Tracker');
  return (
    <>
    <style>{`
      .scroll-container::-webkit-scrollbar {
        display: none;
      }
    `}</style>
    <div className="card card-body p-3">
      {/* Header */}
      <div className="d-flex card-title text-primary justify-content-between align-items-center border-0 mb-2">
        {panelTitle}
        <div className="d-flex gap-2">
          {/* <button
            className="btn btn-sm btn-outline-primary py-0 px-2"
            onClick={() => setQueuePanelOpen(true)}
            title="View Call Queue"
          >
            <i className="fas fa-list-ul"></i>
          </button> */}
          <button
            type="button"
            className="btn btn-sm btn-link text-primary p-0"
            onClick={() => setExpanded(!expanded)}
          >
            <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`}></i>
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show py-2 mb-2" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <small>{error}</small>
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show py-2 mb-2" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          <small>{success}</small>
          <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
          <small>Loading...</small>
        </div>
      ) : expanded && (
        <div className="mt-2">
          {/* Form Fields */}
          <div className="row g-2 mb-2">
            <div className="col-6">
              <label className="form-label fw-semibold mb-1 small">
                {assignedToUserUuid ? "Assigned to" : "Assign to someone"}
              </label>
              <select
                className="form-select form-select-sm"
                value={assignedToUserUuid || ''}
                onChange={(e) => {
                  const selectedUuid = e.target.value || null;
                  setAssignedToUserUuid(selectedUuid);
                  setAssignedToUserType(selectedUuid ? 'sales' : null);
                }}
              >
                <option value="">Unassigned</option>
                {scopedUsers.map(salesUser => (
                  <option key={salesUser.id} value={salesUser.id}>
                    {salesUser.user.first_name} {salesUser.user.last_name} ({salesUser.user.username})
                  </option>
                ))}
              </select>
              {!isCreateMode && assignedToUserUuid && !freshEntityData?.organization && (
                <small className="text-muted d-block mt-1">
                  <i className="fas fa-info-circle me-1"></i>
                  Will auto-assign to user&apos;s organization
                </small>
              )}
            </div>
            <div className="col-6 mb-2">
              <label className="form-label fw-semibold mb-1 small">Lead Score: {leadScore}/10</label>
              <input
                type="range"
                className="form-range"
                min="1"
                max="10"
                value={leadScore}
                onChange={(e) => setLeadScore(parseInt(e.target.value, 10))}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label fw-semibold mb-1 small d-block">Stage</label>
            <div className="position-relative">
              {canScrollStageLeft && (
                <button
                  type="button"
                  className="btn btn-sm btn-light position-absolute start-0 top-50 translate-middle-y"
                  style={{ zIndex: 10, opacity: 0.9 }}
                  onClick={() => scroll(stageScrollRef, 'left')}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
              )}
              <div
                ref={stageScrollRef}
                className="overflow-x-auto scroll-container"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch'
                }}
                onScroll={() => checkScrollState(stageScrollRef, setCanScrollStageLeft, setCanScrollStageRight)}
              >
                <div className="btn-group btn-group-sm w-100" role="group" style={{ flexWrap: 'nowrap', minWidth: '100%' }}>
                  {scopedStatuses.map(status => (
                    <button
                      key={status.id}
                      type="button"
                      className={`btn ${leadStatus === status.name ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setLeadStatus(status.name)}
                      style={{ flex: '1 0 auto' }}
                    >
                      {status.name}
                    </button>
                  ))}
                </div>
              </div>
              {canScrollStageRight && (
                <button
                  type="button"
                  className="btn btn-sm btn-light position-absolute end-0 top-50 translate-middle-y"
                  style={{ zIndex: 10, opacity: 0.9 }}
                  onClick={() => scroll(stageScrollRef, 'right')}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              )}
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label fw-semibold mb-1 small d-block">Priority</label>
            <div className="position-relative">
              {canScrollPriorityLeft && (
                <button
                  type="button"
                  className="btn btn-sm btn-light position-absolute start-0 top-50 translate-middle-y"
                  style={{ zIndex: 10, opacity: 0.9 }}
                  onClick={() => scroll(priorityScrollRef, 'left')}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
              )}
              <div
                ref={priorityScrollRef}
                className="overflow-x-auto scroll-container"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch'
                }}
                onScroll={() => checkScrollState(priorityScrollRef, setCanScrollPriorityLeft, setCanScrollPriorityRight)}
              >
                <div className="btn-group btn-group-sm w-100" role="group" style={{ flexWrap: 'nowrap', minWidth: '100%' }}>
                  {(priorities.length > 0 ? priorities : [
                    { id: 1, name: 'Low' },
                    { id: 2, name: 'Medium' },
                    { id: 3, name: 'High' }
                  ]).map(p => (
                    <button
                      key={p.id}
                      type="button"
                      className={`btn ${priority === p.name ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setPriority(p.name)}
                      style={{ flex: '1 0 auto' }}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
              {canScrollPriorityRight && (
                <button
                  type="button"
                  className="btn btn-sm btn-light position-absolute end-0 top-50 translate-middle-y"
                  style={{ zIndex: 10, opacity: 0.9 }}
                  onClick={() => scroll(priorityScrollRef, 'right')}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              )}
            </div>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-6">
              <label className="form-label fw-semibold mb-1 small">Last Contacted</label>
              <div className="input-group input-group-sm">
                <input
                  type="date"
                  className="form-control"
                  value={lastContactedDate}
                  onChange={(e) => setLastContactedDate(e.target.value)}
                />
                <button type="button" className="btn btn-outline-secondary" onClick={handleTodayClick} title="Set to today">
                  Today
                </button>
              </div>
            </div>
            <div className="col-6">
              <label className="form-label fw-semibold mb-1 small">Follow-up In</label>
              <div className="input-group input-group-sm">
                <input
                  type="date"
                  className="form-control"
                  value={followUpDate}
                  onChange={(e) => setNextFollowUpDate(e.target.value)}
                />
                <button type="button" className="btn btn-outline-secondary" onClick={handleNextWeekClick} title="Set to next week">
                  +1 Week
                </button>
              </div>
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label fw-semibold mb-1 small">Sales Notes</label>
            <textarea
              className="form-control form-control-sm"
              rows="3"
              value={salesNotes}
              onChange={(e) => setSalesNotes(e.target.value)}
              placeholder="Internal notes about this lead..."
            />
          </div>

          {/* Save button - only show in edit mode */}
          {!isCreateMode && (
            <button
              className="btn btn-primary btn-sm w-100 mb-3"
              onClick={handleSave}
              disabled={!hasChanges}
            >
              Update Record
            </button>
          )}

          {/* Tracking History - only show in edit mode */}
          {!isCreateMode && (
          <div className="border-top pt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0 small fw-semibold">Tracking History</h6>
              <button
                className="btn btn-sm btn-link text-primary p-0"
                onClick={() => setHistoryExpanded(!historyExpanded)}
              >
                <i className={`fas fa-chevron-${historyExpanded ? 'up' : 'down'}`}></i>
              </button>
            </div>

            {historyExpanded && (
              <div className="card">
                <div className="card-body p-2" style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {trackingHistory.length === 0 ? (
                    <div className="text-muted text-center py-2">
                      <small><i className="fas fa-history me-2"></i>No tracking history</small>
                    </div>
                  ) : (
                    trackingHistory.map((entry, index) => {
                      const isLast = index === trackingHistory.length - 1;
                      // Look up status and priority names
                      const statusName = contactStatuses.find(s => s.id === entry.contact_status)?.name || entry.contact_status;
                      const priorityName = priorities.find(p => p.id === entry.priority)?.name || entry.priority;

                      return (
                        <div
                          key={entry.id || index}
                          className={`pb-2 mb-2 ${!isLast ? 'border-bottom' : ''}`}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div>
                              <span className="badge badge-sm bg-secondary me-1">
                                {entry.action?.toUpperCase() || 'UPDATED'}
                              </span>
                              <small className="text-muted">
                                by {entry.performed_by?.username || entry.performed_by?.first_name || 'Unknown'}
                              </small>
                              {entry.retell_data?.call_id && (
                                <button
                                  className="btn btn-sm btn-link p-0 ms-2"
                                  onClick={() => window.open(`/analytics?call_id=${entry.retell_data.call_id}`, '_blank')}
                                  title="View call details"
                                >
                                  <i className="fas fa-eye"></i> View
                                </button>
                              )}
                            </div>
                            <small className="text-muted">
                              {new Date(entry.activity_date).toLocaleDateString()}{' '}
                              {new Date(entry.activity_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </small>
                          </div>
                          <div className="small">
                            {entry.prospect_name && <div><strong>Prospect Name:</strong> {entry.prospect_name}</div>}
                            {statusName && <div><strong>Status:</strong> {statusName}</div>}
                            {priorityName && <div><strong>Priority:</strong> {priorityName}</div>}
                            {entry.lead_score !== null && entry.lead_score !== undefined && (
                              <div><strong>Score:</strong> {entry.lead_score}/10</div>
                            )}
                            {entry.last_contacted_date && (
                              <div><strong>Last Contact:</strong> {new Date(entry.last_contacted_date).toLocaleDateString()}</div>
                            )}
                            {entry.follow_up_date && (
                              <div><strong>Next Follow-up:</strong> {new Date(entry.follow_up_date).toLocaleDateString()}</div>
                            )}
                            {entry.notes && <div className="mt-1"><strong>Notes:</strong> {entry.notes}</div>}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </div>

    {/* <CallQueuePanel
      isOpen={queuePanelOpen}
      onClose={() => setQueuePanelOpen(false)}
    /> */}
    </>
  );
};

export default LeadAndOpportunityTracker;
